import type { ComponentProps } from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cn } from '@shared/lib';

/**
 * Underline tab set built on Radix (Figma "Settle up / Export / Group Activity",
 * "Active / Closed / All"). **A11y:** `tablist`/`tab`/`tabpanel` roles, Arrow-key
 * navigation, roving tab order — all from Radix.
 */
export const Tabs = TabsPrimitive.Root;

export function TabsList({ className, ...props }: ComponentProps<typeof TabsPrimitive.List>) {
  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      className={cn('inline-flex items-center gap-8 border-b border-border', className)}
      {...props}
    />
  );
}

export function TabsTrigger({ className, ...props }: ComponentProps<typeof TabsPrimitive.Trigger>) {
  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(
        'relative -mb-px inline-flex items-center gap-2 border-b-[3px] border-transparent px-1 py-5 text-sm font-medium text-muted transition-colors',
        'hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
        'disabled:pointer-events-none disabled:opacity-50',
        'data-[state=active]:font-semibold data-[state=active]:text-fg-strong',
        'after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.75 after:rounded-t-[3px] after:rounded-b-none after:content-[""]',
        'after:bg-transparent data-[state=active]:after:bg-[#161616] dark:data-[state=active]:after:bg-fg-strong',
        className,
      )}
      {...props}
    />
  );
}

export function TabsContent({ className, ...props }: ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn('pt-4 focus-visible:outline-none', className)}
      {...props}
    />
  );
}
